import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { MessageSquare, Send, ArrowLeft, User, ChevronRight, Users, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachMessages = () => {
  const { user, getAuthHeaders } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAthleteList, setShowAthleteList] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
    loadAthletes();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
    setLoading(false);
  };

  const loadAthletes = async () => {
    try {
      const res = await axios.get(`${API}/athletes`, { headers: getAuthHeaders() });
      setAthletes(res.data);
    } catch (error) {
      console.error('Failed to load athletes:', error);
    }
  };

  const startConversation = async (athlete) => {
    try {
      // Get or create conversation for this athlete
      const res = await axios.get(`${API}/conversations/athlete/${athlete.id}`, { headers: getAuthHeaders() });
      setShowAthleteList(false);
      setAthleteSearch('');
      
      // Add to conversations list if not already there
      if (!conversations.find(c => c.id === res.data.id)) {
        setConversations(prev => [res.data, ...prev]);
      }
      
      // Select the conversation
      selectConversation(res.data);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setShowAthleteList(false);
    try {
      const res = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers: getAuthHeaders() });
      setMessages(res.data);
      // Update local unread count
      setConversations(prev => prev.map(c => 
        c.id === conv.id ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  // Filter athletes that don't have a conversation yet
  const filteredAthletes = athletes.filter(a => {
    const hasConversation = conversations.some(c => c.athlete_id === a.id);
    const matchesSearch = a.name?.toLowerCase().includes(athleteSearch.toLowerCase());
    return matchesSearch;
  });

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages`,
        { message_text: newMessage },
        { headers: getAuthHeaders() }
      );
      setMessages([...messages, res.data]);
      setNewMessage('');
      
      // Update conversation list
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, last_message: newMessage.slice(0, 100), last_message_at: res.data.created_at }
          : c
      ));
    } catch (error) {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return '';
    }
  };

  const formatLastMessageTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return format(date, 'MMM d');
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-messages">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Messages</h1>
        <p className="text-muted-foreground">
          Communicate with athletes and parents
        </p>
      </div>

      <div className="bg-card border border-border/40 rounded-sm flex h-[600px]">
        {/* Conversation List */}
        <div className={`${selectedConversation && !showAthleteList ? 'hidden md:block' : 'w-full'} md:w-1/3 border-r border-border/40 overflow-y-auto`}>
          <div className="p-4 border-b border-border/40 flex items-center justify-between">
            <h2 className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
              Conversations ({conversations.length})
            </h2>
            <Button
              size="sm"
              onClick={() => setShowAthleteList(true)}
              className="bg-primary text-black"
            >
              <Plus size={16} className="mr-1" />
              New
            </Button>
          </div>

          {/* Athlete Selection List */}
          {showAthleteList && (
            <div className="border-b border-border/40 bg-muted/20">
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Start conversation with:</p>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={athleteSearch}
                    onChange={(e) => setAthleteSearch(e.target.value)}
                    placeholder="Search athletes..."
                    className="pl-9 bg-background"
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredAthletes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No athletes found</p>
                ) : (
                  filteredAthletes.map(athlete => (
                    <div
                      key={athlete.id}
                      onClick={() => startConversation(athlete)}
                      className="px-4 py-2 hover:bg-muted/30 cursor-pointer flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-heading">
                        {athlete.name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{athlete.name}</p>
                        <p className="text-xs text-muted-foreground">{athlete.position || 'Athlete'}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="p-2 border-t border-border/40">
                <Button size="sm" variant="ghost" onClick={() => setShowAthleteList(false)} className="w-full">
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {conversations.length === 0 && !showAthleteList ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Conversations are created when you access an athlete's messages
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`p-4 cursor-pointer hover:bg-muted/20 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-muted/30' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-heading text-primary">
                        {conv.athlete_name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{conv.athlete_name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                          {conv.last_message || 'No messages'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {conv.unread_count > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 bg-primary text-black text-xs font-bold rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatLastMessageTime(conv.last_message_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-border/40 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  onClick={() => setSelectedConversation(null)}
                >
                  <ArrowLeft size={18} />
                </Button>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-heading text-primary text-sm">
                  {selectedConversation.athlete_name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="font-medium">{selectedConversation.athlete_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedConversation.participants?.map(p => p.name).join(', ')}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No messages yet</p>
                    <p className="text-sm text-muted-foreground">
                      Start the conversation with the parent/athlete
                    </p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwnMessage = msg.sender_id === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%]`}>
                          {!isOwnMessage && (
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                                <User size={12} />
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {msg.sender_name}
                              </span>
                            </div>
                          )}
                          <div
                            className={`px-4 py-2 rounded-lg ${
                              isOwnMessage
                                ? 'bg-primary text-black'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.message_text}</p>
                          </div>
                          <p className={`text-xs text-muted-foreground mt-1 ${isOwnMessage ? 'text-right' : ''}`}>
                            {formatMessageTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border/40">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-primary text-black">
                    <Send size={18} />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Select a conversation</p>
              <p className="text-sm text-muted-foreground mt-1">
                Choose an athlete from the list to view messages
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachMessages;
